import { memo, useContext, useEffect, useState } from 'react';
import { useFormState } from 'react-hook-form';

import { APP_LOCALE, useLanguage } from 'assets/languages';
import GUIDELINE_RULES from 'constants/guidelineRules';
import DOMAIN from 'constants/domain';
import Checkbox from 'components/checkbox';
import TextInput from 'components/textInput';
import Label from 'components/label';
import CampaignFormField from '../../../../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormStepTwoContext } from '../../../../../../campaignFormStepTwo.context';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { GuidelineRuleFormProps, GuidelineRuleFormField } from 'pages/campaigns/campaignForm/campaignForm.types';
import OtherRequestsField from '../../../../../formFields/otherRequestsField/otherRequestsField';
import { BEAUTY_OUTPUT_GROUP_NAMES } from 'pages/campaigns/campaignForm/campaignForm.constants';
import { getRecommendedBeautyOutputGroupNames } from '../../../../../../campaignFormStepTwo.constants';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { isJapanService } from 'constants/domains/domains';
import TextArea from 'components/textArea';

const BeautyOutputFields = () => {
  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.pages.campaigns,
    ...language.component.checkbox,
    ...language.component.reelsGuidelines,
  };
  const { modal, guidelineRuleFormMethods } = useContext(CampaignFormContext);
  const { baseGuidelineRules } = useContext(CampaignFormStepTwoContext);

  const { isDirty: isGuidelineRuleFormDirty } = useFormState(guidelineRuleFormMethods);

  const [beautyOutputRules, setBeautyOutputRules] = useState<
    {
      id: number;
      label: string;
      groupName: keyof GuidelineRuleFormProps;
      recommended: boolean;
      imageUrl: string;
      checked: boolean;
      value: string | null;
    }[]
  >([]);

  const [hasModalOpened, setHasModalOpened] = useState<boolean>(false);

  const onChangeCheckStatus = (index: number) => {
    const currentRule = beautyOutputRules[index];

    // 모달 조건 확인
    const alreadyCheckedCount = beautyOutputRules.filter((r) => r.checked).length;
    if (!currentRule.checked && !hasModalOpened && alreadyCheckedCount >= 4) {
      setHasModalOpened(true);
      modal.showModal({
        hideCloseButton: true,
        isCompact: true,
        title: languageTexts.index_text_5,
        text: languageTexts.index_text_6,
      });
    }

    // react-hook-form 값 설정
    guidelineRuleFormMethods.setValue(
      currentRule.groupName,
      !currentRule.checked ? { id: currentRule.id, value: currentRule.value } : null,
    );

    // 상태 업데이트
    setBeautyOutputRules((prev) =>
      prev.map((rule) =>
        rule.groupName === currentRule.groupName ? { ...rule, checked: !currentRule.checked } : rule,
      ),
    );
  };

  const onChangeInput = (index: number, value: string) => {
    const currentValue = beautyOutputRules[index];

    guidelineRuleFormMethods.setValue(
      currentValue.groupName,
      {
        id: currentValue.id,
        value,
      },
      {
        shouldDirty: true,
      },
    );
    setBeautyOutputRules((prev) =>
      prev.map((rule) => (rule.groupName === currentValue.groupName ? { ...rule, value } : rule)),
    );
  };

  // 초기 필수 장면 설정
  useEffect(() => {
    if (baseGuidelineRules?.length > 0) {
      setBeautyOutputRules(
        baseGuidelineRules
          ?.reduce((acc, baseRule) => {
            if (!BEAUTY_OUTPUT_GROUP_NAMES.includes(baseRule.groupName)) {
              return acc;
            } else {
              let guideImageUrl = '';
              if (baseRule.groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.REELS_PRODUCT_BEFORE_AFTER) {
                guideImageUrl = `${
                  DOMAIN.AWS.S3
                }/guide/reels/${baseRule.groupName.toLowerCase()}_${APP_LOCALE.toLowerCase()}.png`;
              } else {
                guideImageUrl = `${DOMAIN.AWS.S3}/guide/reels/${baseRule.groupName.toLowerCase()}.png`;
              }

              let ruleLabel = GUIDELINE_RULES.GetOptionTranslationMap(baseRule.groupName);
              // 일본 약사법 관련 예외처리
              if (isJapanService() && baseRule.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_BEFORE_AFTER) {
                ruleLabel = languageTexts.create_reels_form_before_after_label_for_japan;
              }

              const rule = {
                label: ruleLabel,
                groupName: baseRule.groupName,
                imageUrl: guideImageUrl,
              };

              // DB에서 가져온 guidelineRule data merge
              const guidelineRule = guidelineRuleFormMethods.getValues(baseRule.groupName) as GuidelineRuleFormField;

              rule['checked'] = !!guidelineRule;
              rule['id'] = guidelineRule?.id;
              rule['value'] = guidelineRule?.value;

              const isRecommendedGroup = getRecommendedBeautyOutputGroupNames().some(
                (recommendedGroupName) => recommendedGroupName === baseRule.groupName,
              );
              rule['recommended'] = isRecommendedGroup;

              return [...acc, rule];
            }
          }, [])
          .sort(
            (a, b) =>
              BEAUTY_OUTPUT_GROUP_NAMES.indexOf(a.groupName as any) -
              BEAUTY_OUTPUT_GROUP_NAMES.indexOf(b.groupName as any),
          ),
      );
    }
  }, [baseGuidelineRules, isGuidelineRuleFormDirty]);

  return (
    <div className="flex flex-col gap-4 mt-[50px] pb-[50px] border-b-[1px] border-[#E8E8E8]">
      <CampaignFormField
        as="div"
        wrapperClassName="!gap-7"
        label={languageTexts.create_reels_form_outputs}
        labelClassName="!text-[17px]"
      >
        <ul className="flex flex-col gap-4">
          {beautyOutputRules?.map((item, index) => {
            return (
              <li
                key={`beauty-output-field-group-${index}`}
                className="flex w-full items-start justify-between gap-3 pb-[24px] border-b-[1px] border-[#E8E8E8]"
              >
                <div className="flex items-center self-start">
                  <Checkbox onClick={() => onChangeCheckStatus(index)} checked={item.checked} />
                </div>
                <img src={item.imageUrl} width="100px" height="100px" className="flex-shrink-0" />
                <div className="flex flex-col flex-grow justify-start self-start gap-2">
                  <div>
                    <div className="flex flex-row gap-2">
                      <p className="text-[15px] font-bold whitespace-pre-wrap tracking-tighter">{item.label}</p>
                      {item.recommended && (
                        <Label
                          className="py-[1px] px-[3px]"
                          textClassName="text-xs"
                          text={languageTexts.recommend}
                          labelColor="black"
                          textColor="white"
                        />
                      )}
                    </div>
                    {isJapanService() &&
                      item.groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.REELS_PRODUCT_BEFORE_AFTER && (
                        <div className="pb-[8px]">
                          <p className="text-[12px]">{languageTexts.index_text_before_after_warning_ja}</p>
                        </div>
                      )}
                  </div>

                  {item.checked && (
                    <div className="max-w-[300px] mt-5">
                      {/* <TextInput
                        bordered
                        placeholder={`${languageTexts.input}`}
                        value={item.value || ''}
                        onChangeText={(value) => onChangeInput(index, value)}
                      /> */}
                      <TextArea
                        bordered
                        placeholder={`${languageTexts.input}`}
                        value={item.value || ''}
                        onChangeText={(value) => onChangeInput(index, value)}
                        autoResize={true}
                        inputStyle={{
                          display: 'block',
                          paddingTop: '12px',
                          paddingBottom: '12px',
                        }}
                      />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CampaignFormField>

      {/* 기타 전달 사항 입력 */}
      <OtherRequestsField />
    </div>
  );
};

export default memo(BeautyOutputFields);
